import React, { useState } from 'react';
import {
  AppBar,
  Box,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import { NavLink } from 'react-router-dom';
import CustomerCategoryCard from '../ui/CustomerCategoryCard';
import AdvisorEmailChip from '../ui/AdvisorEmailChip';
import { useAuth } from '../../context/AuthContext';
import useCustomerTier from '../../hooks/useCustomerTier';

const navigationPages = [
  { label: 'Dashboard', path: '/' },
  { label: 'Onsa Market', path: '/onsa-market' },
  { label: 'Client Boosts', path: '/client-boosts' },
  { label: 'Offer Request', path: '/offer-request' },
  { label: 'Onsa Stock', path: '/onsa-stock' },
  { label: 'Promotions', path: '/promotions' },
  { label: 'Trade AI', path: '/trade-ai' },
  { label: 'Investor Categories', path: '/investor-categories' },
  { label: 'Competition', path: '/competition' },
  { label: 'Friend Referrals', path: '/friend-referrals' },
  { label: 'Feedbacks', path: '/feedback-support' },
];

const HEIGHT_MOBILE = 64;
const HEIGHT_DESKTOP = 72;
const navTextSx = {
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 'inherit',
  fontSize: 15,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
  lineHeight: 1.2,
};

const NavigationBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMdOnly = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isWideDesktop = useMediaQuery('(min-width:1400px)');
  const isCompactNav = useMediaQuery('(max-width:2600px)'); // stack nav items below chips earlier to avoid overlap
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user } = useAuth();
  const { tier: effectiveTier, gradient: tierGradient } = useCustomerTier();
  const showGradient = Boolean(tierGradient);

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  const linkStyle = ({ isActive }) => {
    const activeColor = showGradient ? '#FFFFFF' : '#0b4a99';
    const inactiveColor = showGradient ? 'rgba(255,255,255,0.82)' : '#2c374d';

    return {
      textDecoration: 'none',
      color: isActive ? activeColor : inactiveColor,
      fontWeight: isActive ? '800' : '700',
      fontFamily: 'Poppins, sans-serif',
      paddingBottom: 8,
      paddingInline: 8,
      borderBottom: isActive
        ? `2px solid ${showGradient ? 'rgba(255,255,255,0.88)' : '#0b4a99'}`
        : '2px solid transparent',
      transition: 'color 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
      fontSize: '17px',
      letterSpacing: 0.8,
      display: 'inline-flex',
      alignItems: 'center',
      textShadow: isActive
        ? '0 2px 10px rgba(0,0,0,0.18)'
        : '0 1px 6px rgba(0,0,0,0.12)',
      transform: isActive ? 'translateY(-1px)' : 'none',
    };
  };

  // Extract advisor/admin email with graceful fallbacks
  const adminEmail =
    user?.admin_email ?? user?.adminEmail ?? localStorage.getItem('admin_email');
  const headerOffset = isMobile ? HEIGHT_MOBILE : isCompactNav ? 100 : HEIGHT_DESKTOP;

  return (
    <>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          position: 'fixed',
          backdropFilter: 'saturate(1.1) blur(6px)',
          zIndex: (t) => t.zIndex.appBar,
          transition: 'background-image 0.3s ease, box-shadow 0.3s ease',
          borderBottom: showGradient ? 'none' : '1px solid #eee',
          bgcolor: showGradient ? 'transparent' : 'rgba(255,255,255,0.92)',
          backgroundImage: showGradient ? tierGradient : 'none',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          color: showGradient ? '#FDFDFE' : 'inherit',
          boxShadow: showGradient ? '0 12px 28px rgba(15, 23, 42, 0.24)' : 'none',
        }}
      >
        <Toolbar
          sx={
            isMobile
              ? {
                  minHeight: HEIGHT_MOBILE,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }
              : isCompactNav
              ? {
                  minHeight: headerOffset,
                  px: { md: 2.5, lg: 3 },
                  py: 1.25,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 1.25,
                }
              : {
                  minHeight: HEIGHT_DESKTOP,
                  px: 3,
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  alignItems: 'center',
                  columnGap: 16,
                }
          }
        >
        {isMobile ? (
          <>
            <Box sx={{ pr: 1, flexShrink: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {effectiveTier && <CustomerCategoryCard tier={effectiveTier} compact mobileShort />}
              <Box sx={{ minWidth: 0, maxWidth: { xs: 160, sm: 220 } }}>
                <AdvisorEmailChip email={adminEmail} compact />
              </Box>
            </Box>

            <IconButton edge="end" color="inherit" onClick={() => setDrawerOpen((o) => !o)} aria-label="Open menu">
              <MenuIcon />
            </IconButton>

            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={toggleDrawer(false)}
              // Ensure mobile menu overlays headers and bottom ticker
              sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
              PaperProps={{
                sx: {
                  width: { xs: 'min(88vw, 360px)', sm: 340 },
                  borderTopLeftRadius: 12,
                  borderBottomLeftRadius: 12,
                  overflow: 'hidden',
                  bgcolor: 'rgba(255,255,255,0.92)',
                  backdropFilter: 'saturate(1.1) blur(8px)',
                },
              }}
            >
              <Box sx={{ width: '100%', pb: 'env(safe-area-inset-bottom, 0px)' }} role="presentation">
                {/* Brand header */}
                <Box
                  sx={{
                    pt: 'max(8px, env(safe-area-inset-top, 0px))',
                    pb: 1,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    borderBottom: '1px solid #eee',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: 'rgba(255,255,255,0.92)',
                  }}
                >
                  <Box
                    component="img"
                    alt="OnsaFX"
                    src="https://onsafx.com/wp-content/uploads/2025/03/onsa_icon60x60.svg"
                    sx={{ width: 28, height: 28, borderRadius: 1 }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                    OnsaFX
                  </Typography>
                  <IconButton
                    aria-label="Close menu"
                    onClick={toggleDrawer(false)}
                    sx={{ ml: 'auto' }}
                    size="small"
                  >
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>
                <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                  {effectiveTier && <CustomerCategoryCard tier={effectiveTier} />}
                  <Box sx={{ mt: 1 }}>
                    <AdvisorEmailChip email={adminEmail} size="small" />
                  </Box>
                </Box>
                <List sx={{ py: 1 }}>
                  {navigationPages.map((page) => (
                    <ListItem disablePadding key={page.path}>
                      <ListItemButton
                        component={NavLink}
                        to={page.path}
                        onClick={toggleDrawer(false)}
                        style={({ isActive }) => ({
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: isActive ? '800' : '700',
                          color: isActive ? '#0b4a99' : '#2d3a4d',
                          textDecoration: 'none',
                          textTransform: 'uppercase',
                          backgroundColor: isActive ? 'rgba(13,71,161,0.08)' : 'transparent',
                          borderLeft: isActive ? '3px solid #0d47a1' : '3px solid transparent',
                          letterSpacing: 0.8,
                          fontSize: '15px',
                          borderRadius: 12,
                          textShadow: isActive
                            ? '0 2px 8px rgba(0,0,0,0.15)'
                            : '0 1px 5px rgba(0,0,0,0.08)',
                        })}
                        sx={{
                          px: 2,
                          py: 1.25,
                          '& .MuiListItemIcon-root': { minWidth: 36, color: '#6b7280' },
                        }}
                      >
                        <ListItemIcon>
                          {page.path === '/' && <DashboardOutlinedIcon />}
                          {page.path === '/onsa-market' && <ShowChartOutlinedIcon />}
                          {page.path === '/onsa-stock' && <ShowChartOutlinedIcon />}
                          {page.path === '/client-boosts' && <BoltOutlinedIcon />}
                          {page.path === '/offer-request' && <BoltOutlinedIcon />}
                          {page.path === '/friend-referrals' && <GroupAddOutlinedIcon />}
                          {page.path === '/promotions' && <BoltOutlinedIcon />}
                          {page.path === '/competition' && <EmojiEventsOutlinedIcon />}
                          {page.path === '/investor-categories' && <CategoryOutlinedIcon />}
                          {page.path === '/feedback-support' && <SupportAgentOutlinedIcon />}
                          {page.path === '/trade-ai' && <AutoAwesomeOutlinedIcon />}
                        </ListItemIcon>
                        <ListItemText primary={page.label} primaryTypographyProps={{ sx: navTextSx }} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Drawer>
            </>
          ) : isCompactNav ? (
            <>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.25,
                  minWidth: 0,
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  {effectiveTier && <CustomerCategoryCard tier={effectiveTier} compact />}
                </Box>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <AdvisorEmailChip email={adminEmail} compact />
                </Box>
              </Box>

              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignContent: 'center',
                    gap: { md: 1.25, lg: 1.5 },
                    rowGap: 1.25,
                    width: '100%',
                  }}
                >
                  {navigationPages.map((page) => (
                    <NavLink key={page.path} to={page.path} style={linkStyle}>
                      <Typography variant="body2" sx={navTextSx}>
                        {page.label}
                      </Typography>
                    </NavLink>
                  ))}
                </Box>
              </Box>
            </>
          ) : (
            <>
              {/* Left slot: show Tier on md-only to avoid overlap */}
              <Box sx={{ minWidth: 0 }}>
                {isMdOnly && effectiveTier && <CustomerCategoryCard tier={effectiveTier} compact />}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isWideDesktop ? 'row' : 'column',
                  gap: isWideDesktop ? 4 : 1.25,
                  minWidth: 0,
                  overflow: 'hidden',
                  alignItems: 'center',
                  justifyContent: isWideDesktop ? 'flex-start' : 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: { md: 3, lg: 4 },
                    minWidth: 0,
                    flexWrap: 'wrap',
                    justifyContent: isWideDesktop ? 'flex-start' : 'center',
                    alignContent: 'center',
                    rowGap: 1,
                  }}
                >
                  {navigationPages.map((page) => (
                    <NavLink key={page.path} to={page.path} style={linkStyle}>
                      <Typography variant="body2" sx={navTextSx}>
                        {page.label}
                      </Typography>
                    </NavLink>
                  ))}
                </Box>
              </Box>
              <Box sx={{ justifySelf: 'end', minWidth: 0, maxWidth: { md: 240, lg: 360 }, display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* Show Tier on lg+ only (moved to left on md) */}
                <Box sx={{ display: { xs: 'none', md: 'none', lg: 'inline-flex' } }}>
                  {effectiveTier && <CustomerCategoryCard tier={effectiveTier} compact />}
                </Box>
                {/* Full chip on lg+, compact chip on md */}
                <Box sx={{ display: { xs: 'none', md: 'none', lg: 'inline-flex' } }}>
                  <AdvisorEmailChip email={adminEmail} compact />
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'inline-flex', lg: 'none' }, minWidth: 0 }}>
                  <AdvisorEmailChip email={adminEmail} compact />
                </Box>
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ height: headerOffset }} />
    </>
  );
};

export default NavigationBar;
